import { useState } from 'react';
import { Timestamp, addDoc, collection } from 'firebase/firestore';
import { db } from '../../../firebase';
import { WorkshopTemplate } from '../../../interfaces/template.interface';
import { BookingMode } from '../../../interfaces/workshop.interface';
import { getTypeLabel } from '../../../utils/workshopMigration';

interface Props {
  template: WorkshopTemplate;
  onClose: () => void;
  onCreated: () => void;
}

const AddFromTemplatePopup = ({ template, onClose, onCreated }: Props) => {
  const [date, setDate] = useState('');
  const [bookingMode, setBookingMode] = useState<BookingMode>(BookingMode.SOLO);
  const [priceSolo, setPriceSolo] = useState(0);
  const [priceDuo, setPriceDuo] = useState(0);
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const workshop = {
        // Copie depuis template
        title: template.title,
        description: template.description,
        type: template.type,
        duration: template.duration,
        location: template.location,
        maxParticipants: template.maxParticipants,
        imageUrl: template.imageUrl,

        // Nouveaux champs depuis formulaire
        date: Timestamp.fromDate(new Date(date)),
        bookingMode,
        priceSolo,
        priceDuo,

        // Champs calculés
        remainingPlaces: template.maxParticipants,
        active: true,
        createdBy: '',
        createdAt: Timestamp.now(),
      };

      await addDoc(collection(db, 'workshops_v2'), workshop);
      onCreated();
      onClose();
    } catch (error) {
      console.error('Erreur création atelier depuis template :', error);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-scroll">
      <div className="max-h-[80vh] rounded-lg overflow-y-auto">
        <form
          onSubmit={handleSubmit}
          className="bg-white p-6 rounded-lg shadow-lg w-[800px]"
        >
          <h2 className="text-xl font-semibold mb-4">Ajouter au planning</h2>

          {/* Section infos du template (readonly) */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Informations du template
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500">Titre</label>
                <p className="text-sm font-medium">{template.title}</p>
              </div>

              <div>
                <label className="text-xs text-gray-500">Type</label>
                <p className="text-sm font-medium">
                  {getTypeLabel(template.type)}
                </p>
              </div>

              <div>
                <label className="text-xs text-gray-500">Durée</label>
                <p className="text-sm font-medium">{template.duration} min</p>
              </div>

              <div>
                <label className="text-xs text-gray-500">Max participants</label>
                <p className="text-sm font-medium">{template.maxParticipants}</p>
              </div>

              <div className="col-span-2">
                <label className="text-xs text-gray-500">Lieu</label>
                <p className="text-sm font-medium">{template.location}</p>
              </div>

              {template.description && (
                <div className="col-span-2">
                  <label className="text-xs text-gray-500">Description</label>
                  <div
                    className="text-sm prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: template.description }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Section champs à compléter */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">
              Informations à compléter
            </h3>

            <div>
              <label className="text-sm text-gray-600">Date et heure</label>
              <input
                type="datetime-local"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="border px-3 py-2 rounded w-full"
                required
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Mode de réservation</label>
              <select
                value={bookingMode}
                onChange={(e) => setBookingMode(e.target.value as BookingMode)}
                className="border px-3 py-2 rounded w-full"
              >
                <option value={BookingMode.SOLO}>Individuel uniquement</option>
                <option value={BookingMode.DUO}>Duo uniquement</option>
                <option value={BookingMode.SOLO_DUO}>Individuel ou Duo</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {(bookingMode === BookingMode.SOLO ||
                bookingMode === BookingMode.SOLO_DUO) && (
                <div>
                  <label className="text-sm text-gray-600">Prix Solo (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={priceSolo}
                    onChange={(e) => setPriceSolo(parseFloat(e.target.value) || 0)}
                    className="border px-3 py-2 rounded w-full"
                    required
                    min="0"
                  />
                </div>
              )}

              {(bookingMode === BookingMode.DUO ||
                bookingMode === BookingMode.SOLO_DUO) && (
                <div>
                  <label className="text-sm text-gray-600">Prix Duo (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={priceDuo}
                    onChange={(e) => setPriceDuo(parseFloat(e.target.value) || 0)}
                    className="border px-3 py-2 rounded w-full"
                    required
                    min="0"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-500"
              disabled={creating}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="bg-[#623413] text-white px-4 py-2 rounded hover:opacity-90"
              disabled={creating}
            >
              {creating ? 'Ajout en cours...' : 'Ajouter au planning'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFromTemplatePopup;
